import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { v4 as uuidv4 } from 'uuid'
import { List, Map as ImmutableMap } from 'immutable'
import NestedSelectInput from 'components/common/Inputs/NestedSelectInput'
import { isEmpty } from 'lodash'
import { IconButton } from '@material-ui/core'
import SelectInput from 'components/common/Inputs/SelectInput'
import { ReactComponent as Bin } from 'assets/img/bin.svg'
import { locusFetchList, fetchLocusTree } from 'reducers/locus/reducer'
import { fetchLocusGroupList } from 'reducers/locusGroup/reducer'
import Psfs from 'lib/psfs/Psfs'
import { Site, Unit, UnitGroup, UnitTree, Workflow } from 'types/common'
import {
  FIELD_UNITS,
  LOCUS,
  LOCUS_GROUP,
  PARAMETER_CONTAINER_NAME,
  PARAMETER_ID,
  PARAMETER_NAME,
  FIELD_SITE,
} from 'constants/managementUI'
import messages from '../../translate'
import { formatList } from '../PlanNewJob.utils'
import {
  SelectContainer,
  SiteAndUnitsWrapper,
  RowContainer,
} from './SiteAndUnitsTable.styles'
import { SiteAndUnitsTableProps, Group } from './SiteAndUnitsTable.types'
import { LocusesAndGroups } from '../PlanNewJob.types'
import {
  generateLocusesAndGroups,
  generateValueFromLocusesAndGroups,
} from './SiteAndUnitsTable.utils'

const SiteAndUnitsTable = ({
  intl: { formatMessage },
  siteList,
  isDisabled,
  selectedSites,
  onChangeFormValue,
  workflowInfo,
  locusFetchList,
  fetchLocusTree,
  fetchLocusGroupList,
  unitGroupList,
  unitList,
  unitTree,
  unitsLoading,
  unitsGroupLoading,
  validationErrors,
  setValidationErrors,
  isLoadSiteList,
}: SiteAndUnitsTableProps & WrappedComponentProps) => {
  const { needs_locus, needs_locus_group } = useMemo(
    () => workflowInfo.toJS(),
    [JSON.stringify(workflowInfo)],
  )
  const sites = useMemo(() => siteList.toJS(), [JSON.stringify(siteList)])

  const getUnitTree = (site: number) => {
    const psfs = new Psfs()
    psfs
      .addFilter('site', site)
      .addFilter('active', 1)
      .addFilter('visibleInUi', 1)

    fetchLocusTree({ psfs })
  }

  const getUnitList = (site: number) => {
    const psfs = new Psfs()
    psfs
      .addFilter('active', 1)
      .addFilter('visibleInUi', 1)
      .addFilter('site', site)
      .addFields([PARAMETER_ID, PARAMETER_CONTAINER_NAME])

    locusFetchList({ psfs })
  }

  const getUnitGroupList = (site: number) => {
    const psfs = new Psfs()
    psfs
      .addFilter('active', 1)
      .addFilter('visibleInUi', 1)
      .addFilter('site', site)
      .addFields([PARAMETER_ID, PARAMETER_NAME])

    fetchLocusGroupList({ psfs })
  }

  const checkCoincidences = (value: number) => {
    if (needs_locus && needs_locus_group) {
      getUnitTree(value)
    }

    if (needs_locus_group && !needs_locus) {
      getUnitGroupList(value)
    }

    if (needs_locus && !needs_locus_group) {
      getUnitList(value)
    }
  }

  const getTransformedData = (
    data: (Unit | UnitGroup | UnitTree)[],
    type?: string,
  ) => {
    if (!type) {
      return (data as UnitTree[]).map(({ id, name, locuses }) => ({
        id,
        title: name,
        options: locuses.map(locus => ({
          value: `${locus.id}_${id}`,
          label: locus.name,
        })),
      }))
    }

    return formatList(
      data,
      PARAMETER_ID,
      type === LOCUS ? PARAMETER_CONTAINER_NAME : PARAMETER_NAME,
    )
  }

  const getUnitsList = () => {
    if (
      unitTree &&
      needs_locus &&
      needs_locus_group &&
      !isEmpty(unitTree.toJS())
    ) {
      const { groups } = unitTree.toJS()

      return getTransformedData([...groups])
    }

    if (needs_locus_group && !needs_locus && unitGroupList) {
      return getTransformedData(unitGroupList.toArray(), LOCUS_GROUP)
    }

    if (needs_locus && !needs_locus_group && unitList) {
      return getTransformedData(unitList.toArray(), LOCUS)
    }

    return []
  }

  const formattedSiteList = useMemo(
    () => formatList(sites, PARAMETER_ID, PARAMETER_NAME),
    [JSON.stringify(sites)],
  )

  const sitesList = useMemo(
    () => [
      ...selectedSites,
      {
        site: null,
        locusesAndGroups: [],
      },
    ],
    [
      JSON.stringify(selectedSites),
      JSON.stringify(formattedSiteList),
      JSON.stringify(sites),
    ],
  )

  const onUnitsChange = (value: (string | number)[], index: number) => {
    const updatedSites = [...selectedSites]
    const newValidationErrors = { ...validationErrors }

    delete newValidationErrors[FIELD_UNITS]
    setValidationErrors(newValidationErrors)

    let locusesAndGroups: LocusesAndGroups[] = []
    if (
      needs_locus &&
      needs_locus_group &&
      !unitsLoading &&
      !unitsGroupLoading
    ) {
      locusesAndGroups = generateLocusesAndGroups(
        getUnitsList() as Group[],
        value as string[],
      )
    }

    if (needs_locus_group && !needs_locus) {
      locusesAndGroups = value.map(val => ({
        locusGroup: val,
      }))
    }

    if (needs_locus && !needs_locus_group) {
      locusesAndGroups = value.map(val => ({
        locus: val,
      }))
    }

    updatedSites[index] = {
      ...updatedSites[index],
      locusesAndGroups,
    }
    onChangeFormValue(updatedSites, FIELD_SITE)
  }

  return (
    <SiteAndUnitsWrapper>
      {sitesList.map(({ site, locusesAndGroups }, index) => {
        const siteOptions = formattedSiteList.filter(
          ({ value }) =>
            value === site ||
            !selectedSites.find(selectedSite => selectedSite.site === value) ||
            needs_locus ||
            needs_locus_group,
        )

        const isLoading = unitsLoading || unitsGroupLoading
        const list = isLoading ? [] : getUnitsList()
        const unitsSelectDisabled =
          isDisabled || (!needs_locus && !needs_locus_group) || !site
        const areUnitsValidationFailed =
          Object.prototype.hasOwnProperty.call(validationErrors, FIELD_UNITS) &&
          !locusesAndGroups.length &&
          site
        const areUnitsRequired = (needs_locus || needs_locus_group) && !!site
        const onOpen = () => site && checkCoincidences(site)

        return (
          <RowContainer key={`${site}_${index}` || uuidv4()} align="center">
            <SelectInput
              required={index === 0}
              isValidationFailed={Object.prototype.hasOwnProperty.call(
                validationErrors,
                FIELD_SITE,
              )}
              disabled={isDisabled || isLoadSiteList}
              hasSearchField
              label={formatMessage(messages.selectSite)}
              options={siteOptions}
              value={site}
              onChange={(value: number) => {
                const updatedSites = [...selectedSites]
                const newValidationErrors = { ...validationErrors }

                delete newValidationErrors[FIELD_SITE]
                setValidationErrors(newValidationErrors)

                updatedSites[index] = {
                  site: value,
                  locusesAndGroups: [],
                }

                onChangeFormValue(updatedSites, FIELD_SITE)
              }}
              isLoading={!siteOptions.length}
            />
            <SelectContainer>
              {needs_locus && needs_locus_group ? (
                <NestedSelectInput
                  onOpen={onOpen}
                  groupSelectable
                  disabled={unitsSelectDisabled}
                  isValidationFailed={areUnitsValidationFailed}
                  required={areUnitsRequired}
                  multiple
                  hasSearchField
                  hasSelectAllOption
                  label={formatMessage(messages.selectUnits)}
                  options={list}
                  value={generateValueFromLocusesAndGroups(
                    list as Group[],
                    locusesAndGroups,
                  )}
                  onChange={(value: (string | number)[]) =>
                    onUnitsChange(value, index)
                  }
                  isLoading={isLoading}
                />
              ) : (
                <SelectInput
                  isLoading={isLoading}
                  onOpen={onOpen}
                  hasSelectAllOption
                  multiple
                  required={areUnitsRequired}
                  isValidationFailed={areUnitsValidationFailed}
                  disabled={unitsSelectDisabled}
                  hasSearchField
                  label={formatMessage(messages.selectUnits)}
                  options={list}
                  value={locusesAndGroups.map(el => el.locus || el.locusGroup)}
                  onChange={(value: (string | number)[]) =>
                    onUnitsChange(value, index)
                  }
                />
              )}
            </SelectContainer>
            <IconButton
              disabled={!site}
              onClick={() => {
                const updatedSites = [...selectedSites]

                updatedSites.splice(index, 1)
                onChangeFormValue(updatedSites, FIELD_SITE)
              }}>
              <Bin />
            </IconButton>
          </RowContainer>
        )
      })}
    </SiteAndUnitsWrapper>
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) =>
      | boolean
      | List<Site>
      | List<UnitTree>
      | List<Unit>
      | List<UnitGroup>
      | ImmutableMap<string, Workflow>
  }) => ({
    siteList: state.getIn(['logbookForm', 'siteList']) as List<Site>,
    workflowInfo: state.getIn(['myJobs', 'workflowInfo']) as ImmutableMap<
      string,
      Workflow
    >,
    unitGroupList: state.getIn(['locusGroup', 'locusGroupList']) as List<
      UnitGroup
    >,
    unitList: state.getIn(['locus', 'listData']) as List<Unit>,
    unitsLoading: state.getIn(['locus', 'unitsLoading']) as boolean,
    unitsGroupLoading: state.getIn(['locusGroup', 'isLoadList']) as boolean,
    unitTree: state.getIn(['locus', 'locusTree']) as List<UnitTree>,
    isLoadSiteList: state.getIn(['logbookForm', 'isLoadSiteList']) as boolean,
  }),
  {
    locusFetchList,
    fetchLocusTree,
    fetchLocusGroupList,
  },
)(injectIntl(SiteAndUnitsTable))
